/**
 * The currencies the server accepts, asked once per page load and shared by
 * every form that offers one — the list cannot change while the server runs.
 *
 * Exported through `module.tsx`, so another module's form offers exactly what
 * the server will take, rather than what its own browser happens to know.
 */
import { useEffect, useState } from 'react';
import type { CurrencyInfoDto } from '@forsys/shared';
import { listCurrencies } from './api';

let shared: Promise<readonly CurrencyInfoDto[]> | null = null;

function load(): Promise<readonly CurrencyInfoDto[]> {
  if (!shared) {
    shared = listCurrencies().then(
      (response) => response.currencies,
      (error: unknown) => {
        // A failure is not cached: the next form to open asks again.
        shared = null;
        throw error;
      },
    );
  }
  return shared;
}

export interface Currencies {
  /** ISO 4217 codes, sorted. Empty until loaded. */
  readonly codes: readonly string[];
  /** Decimals a currency's amounts have; 2 until the list is loaded. */
  readonly digitsOf: (code: string) => number;
  readonly loading: boolean;
}

export function useCurrencies(): Currencies {
  const [list, setList] = useState<readonly CurrencyInfoDto[] | null>(null);

  useEffect(() => {
    let current = true;
    load().then(
      (loaded) => {
        if (current) setList(loaded);
      },
      () => {
        // The field still works as a list of nothing; the server is the check.
        if (current) setList([]);
      },
    );
    return () => {
      current = false;
    };
  }, []);

  const digits = new Map((list ?? []).map((currency) => [currency.code, currency.digits]));
  return {
    codes: (list ?? []).map((currency) => currency.code),
    digitsOf: (code) => digits.get(code) ?? 2,
    loading: list === null,
  };
}

/** `codes` with `first` moved to the front, in that order — the ones a screen already uses. */
export function preferredFirst(codes: readonly string[], first: readonly string[]): string[] {
  const front = first.filter((code, index) => codes.includes(code) && first.indexOf(code) === index);
  return [...front, ...codes.filter((code) => !front.includes(code))];
}
